import React, { useEffect } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { 
  setBookingStatus, 
  setOperatorNotes, 
  setBookingNotes, 
  selectBookingStatuses,
  selectBookingStatus,
  selectBookingNotes,
  selectOperatorNotes,
  selectReferralSource,
  setReferralSource
} from 'slices/booking/bookingSlice';
import { useSelector } from 'react-redux';
import { selectIsEdit } from 'slices/booking/cartSlice';
interface BookingStatusValues {
  booking_status: string;
  booking_notes: string;
  operator_notes: string;
  referral_source: string;
}

const referralOptions = [
  { label: "Not Specified (default)", value: "Not Specified (default)" },
  { label: "Google", value: "Google" },
  { label: "Yell", value: "Yell" },
  { label: "Facebook", value: "Facebook" },
  { label: "Instagram", value: "Instagram" },
  { label: "Pinterest", value: "Pinterest" },
  { label: "Email", value: "Email" },
  { label: "Leaflets", value: "Leaflets" },
  { label: "Repeat Business", value: "Repeat Business" },
  { label: "Word of Mouth", value: "Word of Mouth" },
  { label: "Other Online Directories", value: "Other Online Directories" }
];


function BookingStatusSection() {
  const dispatch = useDispatch();
  const bookingStatuses = useSelector(selectBookingStatuses);
  const isEdit = useSelector(selectIsEdit);
  const bookingStatus = useSelector(selectBookingStatus);
  const bookingNotes = useSelector(selectBookingNotes);
  const operatorNotes = useSelector(selectOperatorNotes);
  const referralSource = useSelector(selectReferralSource);

  const formik = useFormik<BookingStatusValues>({
    initialValues: isEdit ? {
      booking_status: bookingStatus || '',
      booking_notes: bookingNotes || '',
      operator_notes: operatorNotes || '',
      referral_source: referralSource || ''
    } :{
      booking_status: '',
      booking_notes: '',
      operator_notes: '', 
      referral_source: ''
    },
    validationSchema: yup.object().shape({
      booking_status: yup.string().required('Please select booking status'),
      booking_notes: yup.string(),
      operator_notes: yup.string(),
      referral_source: yup.string(),
    }),
    onSubmit: () => {
      // Submit handled by parent
    },
    validateOnBlur: true,
    validateOnChange: true
  });

  // Update Redux state when form values change
  useEffect(() => {
    dispatch(setBookingStatus(formik.values.booking_status));
    dispatch(setBookingNotes(formik.values.booking_notes));
    dispatch(setOperatorNotes(formik.values.operator_notes));
    dispatch(setReferralSource(formik.values.referral_source));
  }, [formik.values, dispatch]);

  const statusOptions = React.useMemo(() => 
    bookingStatuses
      .filter(status => status.type === 'booking_status')
      .map(status => ({
        value: status.id.toString(),
        label: status.name,
  })), [bookingStatuses]);

  return (
    <div className="booking-status-section">
      <FormGroup>
        <Label for="booking_status">Booking Status <span className="text-danger">*</span></Label>
        <Select
          id="booking_status"
          name="booking_status"
          options={statusOptions}
          value={statusOptions.find(opt => opt.value === formik.values.booking_status) || null}
          onChange={(option) => formik.setFieldValue('booking_status', option?.value || '')}
          onBlur={formik.handleBlur}
          placeholder="Select status"
          classNamePrefix="select"
        />
        {formik.touched.booking_status && formik.errors.booking_status && (
          <div className="text-danger mt-1">{formik.errors.booking_status}</div>
        )}
      </FormGroup>

      <FormGroup>
        <Label for="referral_source">Referral Source</Label>
        <Select
          id="referral_source"
          name="referral_source"
          placeholder="Enter referral source"
          options={referralOptions}
          value={{ label: formik.values.referral_source || "Not Specified (default)", value: formik.values.referral_source || "Not Specified (default)" }}
          onChange={(option) => formik.setFieldValue('referral_source', option?.value || '')}
          onBlur={formik.handleBlur}
          className={formik.touched.referral_source && formik.errors.referral_source ? "is-invalid" : ""}
          // isClearable
        />
        {formik.touched.referral_source && formik.errors.referral_source && (
          <div className="text-danger mt-1">{formik.errors.referral_source}</div>
        )}
      </FormGroup>

      <FormGroup>
        <Label for="booking_notes">Booking Notes</Label>
        <Input
          type="textarea"
          id="booking_notes"
          name="booking_notes"
          rows={4}
          placeholder="Enter any notes about this booking"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.booking_notes}
          className="form-control"
        />
      </FormGroup>

      <FormGroup>
        <Label for="operator_notes">Operator Notes</Label>
        <Input
          type="textarea"
          id="operator_notes"
          name="operator_notes"
          rows={4}
          placeholder="Enter any notes for the operator"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.operator_notes}
          className="form-control"
        />
      </FormGroup>
    </div>
  );
}

export default BookingStatusSection;
